import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Gift } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", city: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await register(form);
      toast.success("Account creato! Verifica la tua email dal profilo.");
      if (data.verify_code) toast.info(`Codice verifica (demo): ${data.verify_code}`, { duration: 8000 });
      navigate("/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 order-2 lg:order-1">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center"><Gift className="h-5 w-5 text-primary-foreground" /></div>
            <span className="font-head font-extrabold text-xl text-primary">Ti Regalo</span>
          </Link>
          <h1 className="font-head font-extrabold text-3xl">Crea il tuo account</h1>
          <p className="text-muted-foreground mt-2">Ricevi 5 crediti di benvenuto.</p>

          <form onSubmit={submit} className="space-y-4 mt-8">
            <div className="space-y-2">
              <Label htmlFor="name">Nome</Label>
              <Input id="name" value={form.name} onChange={set("name")} required data-testid="reg-name" placeholder="Mario Rossi" className="h-11 rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={form.email} onChange={set("email")} required data-testid="reg-email" placeholder="mario@example.it" className="h-11 rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">Città</Label>
              <Input id="city" value={form.city} onChange={set("city")} data-testid="reg-city" placeholder="Milano" className="h-11 rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={form.password} onChange={set("password")} required minLength={6} data-testid="reg-password" placeholder="Almeno 6 caratteri" className="h-11 rounded-xl" />
            </div>
            <Button type="submit" disabled={loading} data-testid="reg-submit" className="w-full h-11 rounded-full">{loading ? "Creazione..." : "Iscriviti"}</Button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-border" /><span className="text-xs text-muted-foreground uppercase tracking-widest">oppure</span><div className="h-px flex-1 bg-border" />
          </div>
          <Button variant="outline" onClick={googleLogin} data-testid="google-register" className="w-full h-11 rounded-full">
            <img src="https://www.google.com/favicon.ico" alt="" className="h-4 w-4 mr-2" /> Continua con Google
          </Button>

          <p className="text-sm text-center text-muted-foreground mt-8">
            Hai già un account? <Link to="/login" className="text-primary font-semibold" data-testid="to-login">Accedi</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src="https://images.unsplash.com/photo-1749451464307-efce2ae4c451?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxjb21tdW5pdHklMjBzaGFyaW5nJTIwaXRlbXN8ZW58MHx8fHwxNzg0OTkyMjI4fDA&ixlib=rb-4.1.0&q=85" alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-accent/30" />
      </div>
    </div>
  );
}
